import { useQuery } from "@tanstack/react-query";
import type { UseQueryOptions } from "@tanstack/react-query";

import type { ReasoningOptionsResponse } from "@/core/types";
import { SHORT_LIVED_CACHE } from "@/lib/constants/cache";
import { useCheckPermission } from "@/features/access-control/hooks/useCheckPermission";

import { acuityKeys } from "../keyFactory";
import { getReasoningOptions } from "../queryFunctions/getReasoningOptions";

type UseReasoningOptionsProps<TData = ReasoningOptionsResponse> = {
  enabled?: boolean;
  select?: (data: ReasoningOptionsResponse) => TData;
};

type ReasoningOptionsQueryOptions = UseQueryOptions<
  ReasoningOptionsResponse,
  unknown,
  ReasoningOptionsResponse,
  ReturnType<typeof acuityKeys.reasoningOptions>
>;

export function useReasoningOptions<TData = ReasoningOptionsResponse>({
  enabled = true,
  select,
}: UseReasoningOptionsProps<TData> = {}) {
  const isPermitted = useCheckPermission({ permissionKey: "acuityScoreReport" });

  return useQuery({ ...optionFactory((isPermitted ?? false) && enabled), select });
}

export function optionFactory(enabled: boolean) {
  return {
    enabled,
    queryFn: getReasoningOptions,
    queryKey: acuityKeys.reasoningOptions(),
    staleTime: SHORT_LIVED_CACHE,
  } satisfies ReasoningOptionsQueryOptions;
}
